import { memo, useMemo, useState, useEffect, lazy } from 'react';
import { FoldState, ItemV5, KeywordType } from '../types';
import { Checkbox, Tag, Tooltip } from 'antd';
import { toLower, get, map } from 'lodash-es';
import Icon, { DownOutlined, LinkOutlined, UpOutlined } from '@ant-design/icons';
import { useBaseContext, useItemContext } from '../Context';
import { useTranslate } from '@tolgee/react';
import SafeImage from 'components/SafeImage';
import styled from '@emotion/styled';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';
import { useStoreInfo } from '../hooks/useStoreInfo';
import Highlighter from 'react-highlight-words';
import { ReactComponent as SimilarSvg } from '@knowledge/assets/similar.svg';
import { TagList } from '@knowledge/common/components/ItemSelectV2/ItemList';
import { SameSellerSkuSelfProps } from './SameSellerSku';
import useModal from 'hooks/useModal';
import { useSelectControl } from '@knowledge/common/helpers/sku';
import { getTranslate } from '@knowledge/common/components/ItemSelectV5/utils/trans';
const Image = styled(SafeImage)`
  width: 80px;
  height: 80px;
`;

const SameSellerSkuModal = lazy(() =>
  import('./SameSellerSku').then((mod) => ({ default: mod.SameSellerSkuModal })),
);

export const ItemRender = memo(
  (props: Omit<PureItemRenderProps, 'spuSelectMap' | 'toggleSku' | 'toggleSpu'>) => {
    const { spuSelectMap, toggleSpu, toggleSku } = useItemContext();
    return (
      <PureItemRender
        {...props}
        spuSelectMap={spuSelectMap}
        toggleSpu={toggleSpu}
        toggleSku={toggleSku}
      />
    );
  },
);

ItemRender.displayName = 'ItemSelectV5ItemRender';
type PureItemRenderProps = {
  item: ItemV5;
  mode?: 'item' | 'sellerSku';
  showSameSellerSkuTag?: boolean;
  sellerSku?: string;
  repack?: () => void;
} & {
  showCheckbox?: boolean;
  spuSelectMap: ReturnType<typeof useSelectControl>['0'];
  toggleSpu: ReturnType<typeof useSelectControl>['2']['toggleSpu'];
  toggleSku: ReturnType<typeof useSelectControl>['2']['toggleSku'];
};

type PureItemCoreProps = PureItemRenderProps & {
  readonly?: boolean;
  skuDisabled?: boolean;
  skuFoldStatus: FoldState;
  tagId?: string[];
  keyword?: string;
  keywordType?: KeywordType;
  showSameSellerSkuTag?: boolean;
};

export const PureItemRender = (props: PureItemRenderProps) => {
  const { skuDisabled, skuFoldStatus, tagId, keyword, keywordType } = useBaseContext();
  return (
    <PureItemCore
      {...props}
      skuDisabled={skuDisabled}
      skuFoldStatus={skuFoldStatus}
      tagId={tagId}
      keyword={keyword}
      keywordType={keywordType}
    />
  );
};

export const PureItemCore = memo((props: PureItemCoreProps) => {
  const {
    readonly = false,
    item,
    mode = 'item',
    sellerSku,
    repack,
    spuSelectMap,
    toggleSku,
    toggleSpu,
    skuDisabled,
    skuFoldStatus,
    tagId,
    keyword,
    keywordType,
    showSameSellerSkuTag,
  } = props;
  const selectedInfo = spuSelectMap?.[item.internalItemId] || {};
  const { t } = useTranslate();
  const {
    platform,
    region,
    externalShopId,
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    internalItemId,
    skuInfo: _skuInfo = [],
    itemUrl,
    itemSku,
    category: _category,
  } = item;
  const xiapibuyEnable = useXiapibuyEnable(region);
  const storeStr = useStoreInfo(item);
  const [open, setOpen] = useState<FoldState>(skuFoldStatus);
  useEffect(() => {
    setOpen(skuFoldStatus);
  }, [skuFoldStatus]);
  const skuInfo = useMemo(() => {
    return mode === 'item'
      ? _skuInfo
      : _skuInfo.filter((sku) => sku.sellerSku.toLowerCase()?.includes(sellerSku?.toLowerCase()!));
  }, [mode, _skuInfo, sellerSku]);
  const { visible, show, hide, meta } = useModal<SameSellerSkuSelfProps>();
  const [category, setCategory] = useState(_category);
  useEffect(() => {
    if (!_category) return;

    const name = _category.name;
    const namePath = _category.namePath;
    Promise.all(map([name, ...namePath], (e) => getTranslate(e))).then(([name, ...namePath]) => {
      setCategory({
        ..._category,
        name,
        namePath,
      });
    });
  }, [_category]);

  return (
    <div>
      {visible && meta && (
        <SameSellerSkuModal {...meta} onCancel={() => hide()} visible={visible} />
      )}
      <div
        className="h-[80px] flex items-center pt-[10px] pb-[12px] pl-[8px]"
        onClick={() => {
          if (!readonly) {
            toggleSpu(internalItemId, !selectedInfo.checked);
          }
        }}
      >
        {!readonly && (
          <Checkbox
            className="!mr-[8px] flex-shrink-0"
            checked={selectedInfo.checked}
            indeterminate={selectedInfo.indeterminate}
            onChange={(e) => {
              toggleSpu(internalItemId, e.target.checked);
            }}
          />
        )}
        <div className="basis-auto overflow-hidden flex flex-grow">
          <div className="flex-shrink-0 !mr-[8px]">
            <Image hasSmallPic="both" src={imageUrl} width={80} height={80} />
          </div>
          <div className="basis-auto flex-grow overflow-hidden flex flex-col">
            <div className="flex-grow">
              <div className="flex items-start">
                <div
                  title={itemName}
                  className="w-full group line-clamp-2 break-all cursor-pointer leading-[18px] hover:text-[#3366ff] min-h-[20px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    let url = itemUrl;
                    if (
                      platform === 'SHOPEE' &&
                      xiapibuyEnable &&
                      region &&
                      url &&
                      !url.includes('.xiapibuy.com')
                    ) {
                      url = `https://${toLower(
                        region,
                      )}.xiapibuy.com/product/${externalShopId}/${spuId}`;
                    }
                    window.open(url, '_blank');
                  }}
                >
                  <LinkOutlined className="!hidden float-right bg-[#d6e8ff] !text-[#336fff] h-[20px] w-[20px] !leading-[20px] group-hover:!block" />
                  {itemName}
                </div>
              </div>
              <div className="flex items-center truncate basis-auto overflow-hidden leading-[18px] text-[12px] text-[#84868c] mt-[4px]">
                <div className="truncate" title={storeStr}>
                  {t('marketing.店铺')}: {storeStr}
                </div>
                <div className="mx-[4px]">|</div>
                <span>ID: {spuId}</span>
                <div className="mx-[4px]">|</div>
                <span className="truncate" title={itemSku || '-'}>
                  {t('aide.主商品货号')}: {itemSku || '-'}
                </span>
              </div>
            </div>
            <div className="mt-[4px] flex-shrink-0">
              <div className="flex justify-between items-center leading-[18px] text-[12px] text-[#84868c]">
                <div>
                  {category && (
                    <Tooltip title={category.namePath.join('-')}>{get(category, 'name')}</Tooltip>
                  )}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(open === 'fold' ? 'unfold' : 'fold');
                    repack?.();
                  }}
                  className="flex-shrink-0 cursor-pointer hover:text-[#3366ff]"
                >
                  {t('knowledge.共{{x}}个SKU', { x: skuInfo?.length })}
                  {open === 'fold' ? (
                    <DownOutlined className="ml-[4px]" />
                  ) : (
                    <UpOutlined className="ml-[4px]" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!skuDisabled && open === 'unfold' && (
        <div className="pl-[32px] flex flex-wrap border-0 border-solid border-[#f0f2f5] [&:not(:empty)]:border-t [&:not(:empty)]:pt-[8px] [&:not(:empty)]:mt-[8px] gap-[8px]">
          {skuInfo.map((sku) => {
            const renderTag = sku.tagSimpleData?.filter((tag) => tagId?.includes(tag.tagId));

            const skuNode = (
              <div className="text-[#515359] flex" title={`${sku.sellerSku}(${sku.skuName})`}>
                <span className="truncate">
                  {keywordType === 4 ? (
                    <Highlighter
                      autoEscape
                      searchWords={[keyword || '']}
                      textToHighlight={`${sku.sellerSku}(${sku.skuName})`}
                    />
                  ) : (
                    `${sku.sellerSku}(${sku.skuName})`
                  )}
                </span>
                {showSameSellerSkuTag &&
                  mode === 'item' &&
                  selectedInfo?.skus?.[sku.skuId]?.checked && (
                    <TagList>
                      <Tag
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          show({
                            internalItemId: internalItemId,
                            sellerSku: sku.sellerSku,
                          });
                        }}
                        className="spec"
                        icon={<Icon component={SimilarSvg} style={{ color: '#336FFF' }} />}
                      >
                        {t('knowledge.item.sellerSku.same')}
                      </Tag>
                    </TagList>
                  )}
                {mode === 'item' &&
                !selectedInfo?.skus?.[sku.skuId]?.checked &&
                tagId &&
                tagId?.length > 0 ? (
                  <TagList>
                    {renderTag?.length > 0 && (
                      <EllipsisTag color="geekblue">{renderTag[0].tagName}</EllipsisTag>
                    )}
                    {renderTag?.length > 1 && (
                      <Tag color="geekblue">
                        {t('knowledge.item.etc', { x: renderTag?.length - 1 })}
                      </Tag>
                    )}
                  </TagList>
                ) : null}
              </div>
            );

            if (skuDisabled) {
              return (
                <div className="sku" key={sku.skuId}>
                  {skuNode}
                </div>
              );
            }

            return (
              <div
                className="w-[calc(50%-13px)] overflow-hidden [&_.ant-checkbox-wrapper]:overflow-hidden [&_.ant-checkbox-wrapper]:w-full [&_.ant-checkbox-wrapper>span:last-of-type]:overflow-hidden"
                key={sku.skuId}
              >
                {readonly ? (
                  skuNode
                ) : (
                  <Checkbox
                    onChange={(e) => toggleSku(internalItemId, sku.skuId, e.target.checked)}
                    checked={selectedInfo?.skus?.[sku.skuId]?.checked}
                  >
                    {skuNode}
                  </Checkbox>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

const EllipsisTag = styled(Tag)`
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

PureItemRender.displayName = 'ItemSelectV5PureItemRender';
